"use client";

import Link from "next/link";
import { JSX } from "react";
import {
  BlogInlineCallout,
  type BlogCalloutKind,
} from "~/components/siteglue/blog/blog-inline-callout";

function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

/** Parse `## Title {#custom-id}` (Pandoc-style) or fall back to a slug of the title. */
function parseHeadingLine(line: string): { text: string; id: string } {
  const raw = line.replace(/^#{1,3}\s+/, "");
  const idMatch = raw.match(/\s*\{#([^}\s]+)\}\s*$/);
  const text = raw.replace(/\s*\{#([^}\s]+)\}\s*$/, "").trim();
  const id = (idMatch?.[1] ?? slugifyHeading(text)).trim();
  return { text, id };
}

function renderInline(text: string, key: string): (string | JSX.Element)[] {
  const tokens: (string | JSX.Element)[] = [];
  let cursor = 0;
  let m: RegExpExecArray | null;
  const tokenRegex = /\*\*(.*?)\*\*|\*(.*?)\*|\[([^\]]+)\]\(([^)]+)\)/g;
  while ((m = tokenRegex.exec(text)) !== null) {
    if (m.index > cursor) tokens.push(text.substring(cursor, m.index));
    if (m[1] !== undefined) tokens.push(<strong key={`b${m.index}`}>{m[1]}</strong>);
    else if (m[2] !== undefined) tokens.push(<em key={`i${m.index}`}>{m[2]}</em>);
    else if (m[3] !== undefined) {
      const href = m[4];
      const isHash = href.startsWith("#");
      const isInternal = href.startsWith("/");
      if (isHash) {
        tokens.push(
          <a
            key={`a${m.index}`}
            href={href}
            className="text-primary hover:underline"
            onClick={(e) => {
              const id = href.slice(1);
              const el = typeof document !== "undefined" ? document.getElementById(id) : null;
              if (!el) return;
              e.preventDefault();
              el.scrollIntoView({ behavior: "smooth", block: "start" });
              if (typeof window !== "undefined") {
                window.history.replaceState(null, "", href);
              }
            }}
          >
            {m[3]}
          </a>,
        );
      } else if (isInternal) {
        tokens.push(
          <Link key={`a${m.index}`} href={href} className="text-primary hover:underline">
            {m[3]}
          </Link>,
        );
      } else {
        tokens.push(
          <a
            key={`a${m.index}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            {m[3]}
          </a>,
        );
      }
    }
    cursor = m.index + m[0].length;
  }
  if (cursor < text.length) tokens.push(text.substring(cursor));
  return tokens;
}

const headingScrollClass = "scroll-mt-28";

const CALLOUT_KINDS = new Set<BlogCalloutKind>([
  "hitl",
  "opportunity",
  "impact",
  "employee",
  "demo",
  "founder",
]);

function parseCalloutKind(raw: string): BlogCalloutKind | null {
  const kind = raw.trim().toLowerCase() as BlogCalloutKind;
  return CALLOUT_KINDS.has(kind) ? kind : null;
}

export function renderMarkdown(content: string): JSX.Element[] {
  const lines = content.split("\n");
  const elements: JSX.Element[] = [];
  let listItems: string[] = [];
  let inTable = false;
  let tableRows: string[][] = [];
  let tableHeader: string[] = [];
  let calloutKind: BlogCalloutKind | null = null;
  let calloutLines: string[] = [];

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`list-${elements.length}`} className="mb-5 list-inside list-disc space-y-1.5 pl-2 text-muted-foreground">
          {listItems.map((item, i) => (
            <li key={i} className="leading-relaxed">
              {renderInline(item, `li-${i}`)}
            </li>
          ))}
        </ul>,
      );
      listItems = [];
    }
  };

  const flushTable = () => {
    if (tableRows.length > 0) {
      elements.push(
        <div key={`table-${elements.length}`} className="mb-6 overflow-x-auto">
          <table className="w-full border-collapse border border-border text-sm">
            {tableHeader.length > 0 && (
              <thead>
                <tr className="bg-muted/50">
                  {tableHeader.map((h, i) => (
                    <th key={i} className="border border-border px-4 py-2 text-left font-semibold">
                      {renderInline(h.trim(), `th-${i}`)}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {tableRows.map((row, ri) => (
                <tr key={ri} className={ri % 2 === 0 ? "bg-background" : "bg-muted/20"}>
                  {row.map((cell, ci) => (
                    <td key={ci} className="border border-border px-4 py-2 text-muted-foreground">
                      {renderInline(cell.trim(), `td-${ri}-${ci}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      tableRows = [];
      tableHeader = [];
      inTable = false;
    }
  };

  const flushCallout = () => {
    if (!calloutKind) return;
    const body = calloutLines.join("\n").trim();
    if (calloutKind === "founder") {
      elements.push(
        <BlogInlineCallout
          key={`callout-${elements.length}`}
          kind="founder"
          insight={body}
        />,
      );
    } else {
      elements.push(
        <BlogInlineCallout key={`callout-${elements.length}`} kind={calloutKind}>
          {body.split("\n").map((line, idx) => (
            <p key={idx} className={idx === 0 ? undefined : "mt-2"}>
              {renderInline(line, `callout-${idx}`)}
            </p>
          ))}
        </BlogInlineCallout>,
      );
    }
    calloutKind = null;
    calloutLines = [];
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? "";

    const openCallout = line.match(/^:::\s*([a-zA-Z-]+)\s*$/);
    if (openCallout && !calloutKind) {
      flushList();
      flushTable();
      const kind = parseCalloutKind(openCallout[1] ?? "");
      if (kind) {
        calloutKind = kind;
        calloutLines = [];
        continue;
      }
    }
    if (calloutKind) {
      if (line.match(/^:::\s*$/)) {
        flushCallout();
        continue;
      }
      calloutLines.push(line);
      continue;
    }

    if (line.match(/^\|.*\|/)) {
      if (line.match(/^[\|\s\-:]+$/)) continue;
      const cells = line.split("|").slice(1, -1);
      if (!inTable && tableHeader.length === 0) {
        tableHeader = cells;
        inTable = true;
      } else {
        tableRows.push(cells);
      }
      continue;
    } else if (inTable) {
      flushTable();
    }

    if (line.match(/^---+\s*$/)) {
      flushList();
      elements.push(<hr key={`hr-${i}`} className="my-8 border-border" />);
      continue;
    }

    if (line.match(/^# /)) {
      flushList();
      const { text, id } = parseHeadingLine(line);
      elements.push(
        <h1
          key={i}
          id={id}
          className={`mb-6 mt-2 text-3xl font-bold tracking-tight sm:text-4xl ${headingScrollClass}`}
        >
          {renderInline(text, `h1-${i}`)}
        </h1>,
      );
    } else if (line.match(/^## /)) {
      flushList();
      const { text, id } = parseHeadingLine(line);
      elements.push(
        <h2 key={i} id={id} className={`mb-4 mt-10 text-2xl font-bold ${headingScrollClass}`}>
          {renderInline(text, `h2-${i}`)}
        </h2>,
      );
    } else if (line.match(/^### /)) {
      flushList();
      const { text, id } = parseHeadingLine(line);
      elements.push(
        <h3 key={i} id={id} className={`mb-3 mt-7 text-xl font-semibold ${headingScrollClass}`}>
          {renderInline(text, `h3-${i}`)}
        </h3>,
      );
    } else if (line.match(/^[-*]\s+/)) {
      listItems.push(line.replace(/^[-*]\s+/, ""));
    } else if (line.trim() === "") {
      flushList();
    } else {
      flushList();
      elements.push(
        <p key={i} className="mb-5 leading-relaxed text-muted-foreground">
          {renderInline(line, `p-${i}`)}
        </p>,
      );
    }
  }
  flushList();
  flushTable();
  flushCallout();
  return elements;
}
